import { createRouter, createWebHistory } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import { useSettingsStore } from '@/stores/settings'

// Frontend Pages
import HomePage from '@/views/frontend/HomePage.vue'
import AboutPage from '@/views/frontend/AboutPage.vue'
import ServicesPage from '@/views/frontend/ServicesPage.vue'
import ServiceDetailPage from '@/views/frontend/ServiceDetailPage.vue'
import PortfolioPage from '@/views/frontend/PortfolioPage.vue'
import PortfolioDetailPage from '@/views/frontend/PortfolioDetailPage.vue'
import ContactPage from '@/views/frontend/ContactPage.vue'
import PrivacyPolicyPage from '@/views/frontend/PrivacyPolicyPage.vue'
import TermsOfServicePage from '@/views/frontend/TermsOfServicePage.vue'
import CookiePolicyPage from '@/views/frontend/CookiePolicyPage.vue'
import DataDeletionPolicyPage from '@/views/frontend/DataDeletionPolicyPage.vue'
import ProductsPage from '@/views/frontend/ProductsPage.vue'
import ProductDetailPage from '@/views/frontend/ProductDetailPage.vue'
import DevProfileView from '@/views/frontend/DevProfileView.vue'
import MaintenancePage from '@/views/frontend/MaintenancePage.vue'


// Auth Pages
import LoginPage from '@/views/auth/LoginPage.vue'
import RegisterPage from '@/views/auth/RegisterPage.vue'
import ForgotPassword from '@/views/auth/ForgotPassword.vue'
import ResetPassword from '@/views/auth/ResetPassword.vue'

// Admin Dashboard Pages
import AdminOverview from '@/views/admin/AdminOverview.vue'
import DeveloperOverview from '@/views/developer/DeveloperOverview.vue'
import DeveloperProfile from '@/views/developer/DeveloperProfile.vue'
import ClientOverview from '@/views/client/ClientOverview.vue'
import HomeContentManagement from '@/views/dashboard/HomeContentManagement.vue'
import ServicesContentManagement from '@/views/dashboard/ServicesContentManagement.vue'
import PortfolioContentManagement from '@/views/dashboard/PortfolioContentManagement.vue'
import AboutContentManagement from '@/views/dashboard/AboutContentManagement.vue'
import ContactContentManagement from '@/views/dashboard/ContactContentManagement.vue'
import ServiceManagement from '@/views/dashboard/ServiceManagement.vue'
import OrderManagement from '@/views/dashboard/OrderManagement.vue'
import UserManagement from '@/views/dashboard/UserManagement.vue'
import Settings from '@/views/dashboard/Settings.vue'
import LegalPagesManagement from '@/views/dashboard/LegalPagesManagement.vue'
import ProductManagement from '@/views/dashboard/ProductManagement.vue'

// Client Dashboard Pages
import ServiceOrder from '@/views/client/ServiceOrder.vue'
import OrderHistory from '@/views/client/OrderHistory.vue'
import OrderPayment from '@/views/client/OrderPayment.vue'
import PaymentSuccess from '@/views/client/PaymentSuccess.vue'
import InvoiceView from '@/views/client/InvoiceView.vue'
import OrderDetails from '@/views/shared/OrderDetails.vue'
import Profile from '@/views/client/Profile.vue'

const routes = [
  // Public Routes
  {
    path: '/',
    name: 'Home',
    component: HomePage,
    meta: {
      title: 'Home - Ruxzom',
      requiresAuth: false
    }
  },
  {
    path: '/about',
    name: 'About',
    component: AboutPage,
    meta: {
      title: 'About Us - Ruxzom',
      requiresAuth: false
    }
  },
  {
    path: '/services',
    name: 'Services',
    component: ServicesPage,
    meta: {
      title: 'Our Services - Ruxzom',
      requiresAuth: false
    }
  },
  {
    path: '/services/:id',
    name: 'ServiceDetail',
    component: ServiceDetailPage,
    meta: {
      title: 'Service Details - Ruxzom',
      requiresAuth: false
    }
  },
  {
    path: '/portfolio',
    name: 'Portfolio',
    component: PortfolioPage,
    meta: {
      title: 'Portfolio - Ruxzom',
      requiresAuth: false
    }
  },
  {
    path: '/portfolio/:id',
    name: 'PortfolioDetail',
    component: PortfolioDetailPage,
    meta: {
      title: 'Portfolio Details - Ruxzom',
      requiresAuth: false
    }
  },
  {
    path: '/products',
    name: 'Products',
    component: ProductsPage,
    meta: {
      title: 'Our Products - Ruxzom',
      requiresAuth: false
    }
  },
  {
    path: '/products/:id',
    name: 'ProductDetail',
    component: ProductDetailPage,
    meta: {
      title: 'Product Details - Ruxzom',
      requiresAuth: false
    }
  },
  {
    path: '/contact',
    name: 'Contact',
    component: ContactPage,
    meta: {
      title: 'Contact Us - Ruxzom',
      requiresAuth: false
    }
  },
  {
    path: '/profile/:slug',
    name: 'DevProfile',
    component: DevProfileView,
    meta: {
      title: 'Developer Profile - Ruxzom',
      requiresAuth: false
    }
  },

  {
    path: '/privacy-policy',
    name: 'PrivacyPolicy',
    component: PrivacyPolicyPage,
    meta: {
      title: 'Privacy Policy - Ruxzom',
      requiresAuth: false
    }
  },
  {
    path: '/terms-of-service',
    name: 'TermsOfService',
    component: TermsOfServicePage,
    meta: {
      title: 'Terms of Service - Ruxzom',
      requiresAuth: false
    }
  },

  {
    path: '/data-deletion-policy',
    name: 'Data Deletion Policy',
    component: DataDeletionPolicyPage,
    meta: {
      title: 'Data Deletion Policy - Ruxzom',
      requiresAuth: false
    }
  },
  {
    path: '/maintenance',
    name: 'Maintenance',
    component: MaintenancePage,
    meta: {
      title: 'Maintenance - Ruxzom',
      requiresAuth: false
    }
  },

  // Auth Routes
  {
    path: '/login',
    name: 'Login',
    component: LoginPage,
    meta: {
      title: 'Login - Ruxzom',
      requiresAuth: false,
      redirectIfAuth: true
    }
  },
  {
    path: '/register',
    name: 'Register',
    component: RegisterPage,
    meta: {
      title: 'Register - Ruxzom',
      requiresAuth: false,
      redirectIfAuth: true
    }
  },
  {
    path: '/forgot-password',
    name: 'ForgotPassword',
    component: ForgotPassword,
    meta: {
      title: 'Forgot Password - Ruxzom',
      requiresAuth: false,
      redirectIfAuth: true
    }
  },
  {
    path: '/reset-password/:token',
    name: 'ResetPassword',
    component: ResetPassword,
    meta: {
      title: 'Reset Password - Ruxzom',
      requiresAuth: false,
      redirectIfAuth: true
    }
  },

  // Dashboard Routes
  {
    path: '/dashboard',
    meta: { requiresAuth: true },
    children: [
      // Dashboard Root Redirect
      {
        path: '',
        name: 'Dashboard',
        beforeEnter: (to, from, next) => {
          const auth = useAuthStore()
          if (auth.user?.role === 'admin') next('/dashboard/admin')
          else if (auth.user?.role === 'developer') next('/dashboard/developer')
          else next('/dashboard/client')
        }
      },

      // Admin Area
      {
        path: 'admin',
        meta: { requiresRole: ['admin', 'developer'] },
        children: [
          {
            path: '',
            name: 'AdminDashboard',
            component: AdminOverview,
            meta: { title: 'Admin Overview' }
          },
          {
            path: 'content/home',
            name: 'HomeContentManagement',
            component: HomeContentManagement,
            meta: { title: 'Home Content' }
          },
          {
            path: 'content/services',
            name: 'ServicesContentManagement',
            component: ServicesContentManagement,
            meta: { title: 'Services Content' }
          },
          {
            path: 'content/portfolio',
            name: 'PortfolioContentManagement',
            component: PortfolioContentManagement,
            meta: { title: 'Portfolio Content' }
          },
          {
            path: 'content/about',
            name: 'AboutContentManagement',
            component: AboutContentManagement,
            meta: { title: 'About Content' }
          },
          {
            path: 'content/dev-profiles',
            name: 'DevProfilesContentManagement',
            component: () => import('@/views/dashboard/DevProfilesContentManagement.vue'),
            meta: { title: 'Dev Profiles' }
          },
          {
            path: 'content/contact',
            name: 'ContactContentManagement',
            component: ContactContentManagement,
            meta: { title: 'Contact Content' }
          },
          {
            path: 'services',
            name: 'ServiceManagement',
            component: ServiceManagement,
            meta: { title: 'Service Management' }
          },
          {
            path: 'orders',
            name: 'AdminOrders',
            component: OrderManagement,
            meta: { title: 'Order Management' }
          },
          {
            path: 'orders/:id',
            name: 'AdminOrderDetails',
            component: OrderDetails,
            meta: { title: 'Order Details' }
          },
          {
            path: 'users',
            name: 'UserManagement',
            component: UserManagement,
            meta: { title: 'User Management', requiresRole: 'admin' }
          },
          {
            path: 'legal-pages',
            name: 'LegalPagesManagement',
            component: LegalPagesManagement,
            meta: { title: 'Legal Pages', requiresRole: 'admin' }
          },
          {
            path: 'settings',
            name: 'Settings',
            component: Settings,
            meta: { title: 'Settings', requiresRole: 'admin' }
          },
          {
            path: 'products',
            name: 'ProductManagement',
            component: ProductManagement,
            meta: { title: 'Product Management' }
          },
          {
            path: 'activity-logs',
            name: 'ActivityLogs',
            component: () => import('@/views/admin/ActivityLog.vue'),
            meta: { title: 'Activity Logs', requiresRole: 'admin' }
          },
          {
            path: 'dev-assistant',
            name: 'DevAssistant',
            component: () => import('@/views/dashboard/DevAssistant.vue'),
            meta: { title: 'Developer Assistant' }
          },
          {
            path: 'profile',
            name: 'AdminProfile',
            component: Profile,
            meta: { title: 'Admin Profile', requiresRole: 'admin' }
          },
        ]
      },

      // Developer Area
      {
        path: 'developer',
        meta: { requiresRole: 'developer' },
        children: [
          {
            path: '',
            name: 'DeveloperDashboard',
            component: DeveloperOverview,
            meta: { title: 'Developer Overview' }
          },
          {
            path: 'orders',
            name: 'DevOrders',
            component: OrderManagement,
            meta: { title: 'Assigned Orders' }
          },
          {
            path: 'orders/:id',
            name: 'DevOrderDetails',
            component: OrderDetails,
            meta: { title: 'Order Details' }
          },
          {
            path: 'products',
            name: 'DevProducts',
            component: ProductManagement,
            meta: { title: 'Assigned Products' }
          },
          {
            path: 'profile',
            name: 'DevProfileSettings',
            component: DeveloperProfile,
            meta: { title: 'My Profile' }
          }
        ]
      },

      // Client Area
      {
        path: 'client',
        meta: { requiresRole: 'client' },
        children: [
          {
            path: '',
            name: 'ClientDashboard',
            component: ClientOverview,
            meta: { title: 'Overview' }
          },
          {
            path: 'orders',
            name: 'OrderHistory',
            component: OrderHistory,
            meta: { title: 'My Orders' }
          },
          {
            path: 'orders/:id',
            name: 'ClientOrderDetails',
            component: OrderDetails,
            meta: { title: 'Order Details' }
          },
          {
            path: 'profile',
            name: 'Profile',
            component: Profile,
            meta: { title: 'My Profile' }
          }
        ]
      }
    ]
  },

  // Legacy/Shared Client Routes (keeping these for compatibility if needed, or redirecting)
  {
    path: '/order-service',
    name: 'ServiceOrder',
    component: ServiceOrder,
    meta: {
      title: 'Order Service - Ruxzom',
      requiresAuth: true,
      requiresRole: 'client'
    }
  },
  {
    path: '/orders/:id/pay',
    name: 'OrderPayment',
    component: OrderPayment,
    meta: {
      title: 'Payment - Ruxzom',
      requiresAuth: true,
      requiresRole: 'client'
    }
  },
  {
    path: '/orders/:id/success',
    name: 'PaymentSuccess',
    component: PaymentSuccess,
    meta: {
      title: 'Payment Successful - Ruxzom',
      requiresAuth: true,
      requiresRole: 'client'
    }
  },
  {
    path: '/orders/:id/invoice',
    name: 'InvoiceView',
    component: InvoiceView,
    meta: {
      title: 'Invoice - Ruxzom',
      requiresAuth: true,
      requiresRole: 'client'
    }
  },

  // Catch all 404
  {
    path: '/:pathMatch(.*)*',
    name: 'NotFound',
    component: () => import('@/views/NotFound.vue'),
    meta: {
      title: '404 - Page Not Found',
      requiresAuth: false
    }
  }
]

const router = createRouter({
  history: createWebHistory(),
  routes,
  scrollBehavior(to, from, savedPosition) {
    if (savedPosition) {
      return savedPosition
    } else {
      return { top: 0 }
    }
  }
})

// Navigation Guards
router.beforeEach(async (to, from, next) => {
  const authStore = useAuthStore()
  const settingsStore = useSettingsStore()

  // Set page title
  document.title = to.meta.title || 'Ruxzom'

  // Initialize auth store if not already done
  if (!authStore.initialized) {
    await authStore.initializeAuth()
  }

  // Check Maintenance Mode
  if (settingsStore.settings.maintenance_mode === '1' &&
    to.name !== 'Maintenance' &&
    !to.path.startsWith('/dashboard/admin') &&
    authStore.user?.role !== 'admin' &&
    authStore.user?.role !== 'developer'
  ) {
    next('/maintenance')
    return
  }

  // Redirect away from maintenance if mode is off
  if (settingsStore.settings.maintenance_mode !== '1' && to.name === 'Maintenance') {
    next('/')
    return
  }

  // Check authentication requirements
  if (to.meta.requiresAuth || to.matched.some(record => record.meta.requiresAuth)) {
    if (!authStore.isAuthenticated) {
      // Store the intended route for redirect after login
      sessionStorage.setItem('redirectAfterLogin', to.fullPath)
      next('/login')
      return
    }

    // Check role requirements from matched routes
    const matchedWithRole = to.matched.find(record => record.meta.requiresRole);
    const requiredRole = matchedWithRole ? matchedWithRole.meta.requiresRole : null;

    if (requiredRole) {
      const allowedRoles = Array.isArray(requiredRole) ? requiredRole : [requiredRole];
      if (!allowedRoles.includes(authStore.user?.role)) {
        // Redirect based on role
        if (authStore.user?.role === 'admin') next('/dashboard/admin')
        else if (authStore.user?.role === 'developer') next('/dashboard/developer')
        else next('/dashboard/client')
        return
      }
    }
  }

  // Redirect authenticated users away from auth pages
  if ((to.path === '/login' || to.path === '/register') && authStore.isAuthenticated) {
    const targetPath = authStore.user?.role === 'admin'
      ? '/dashboard/admin'
      : (authStore.user?.role === 'developer' ? '/dashboard/developer' : '/dashboard/client')
    next(targetPath)
    return
  }

  next()
})

export default router
